import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Avatar,
  IconButton,
  Box,
  FormControl,
  FormHelperText
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { getPhotoUrl } from '../../services/api';

const ContactForm = ({ open, onClose, onSubmit, contact = null, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone1: '',
    phone2: '',
    countryCode1: '55',
    countryCode2: '55',
    email: '',
    shortDescription: '',
    sendWhatsAppDiary: false,
    isMainContact: false,
    photo: ''
  });
  const [errors, setErrors] = useState({});
  const [previewPhoto, setPreviewPhoto] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (contact) {
        // Resetar o estado antes de definir os novos dados
        setPreviewPhoto(null);
        
        // Extrair código do país e número de telefone, se existirem
        let phone1 = contact.phone1 || '';
        let phone2 = contact.phone2 || '';
        let countryCode1 = '55';
        let countryCode2 = '55';
        
        // Se o telefone já tiver código do país, extraí-lo
        if (phone1 && phone1.startsWith('+')) {
          const match = phone1.match(/^\+(\d+)\s(.+)$/);
          if (match) {
            countryCode1 = match[1];
            phone1 = match[2];
          } else {
            // Caso não consiga extrair no formato esperado, usa apenas o código do país
            countryCode1 = phone1.replace(/^\+(\d+).*$/, '$1');
            phone1 = '';
          }
        }
        
        if (phone2 && phone2.startsWith('+')) {
          const match = phone2.match(/^\+(\d+)\s(.+)$/);
          if (match) {
            countryCode2 = match[1];
            phone2 = match[2];
          } else {
            // Caso não consiga extrair no formato esperado, usa apenas o código do país
            countryCode2 = phone2.replace(/^\+(\d+).*$/, '$1');
            phone2 = '';
          }
        }
        
        setFormData({
          name: contact.name || '',
          phone1: phone1,
          phone2: phone2,
          countryCode1: countryCode1,
          countryCode2: countryCode2,
          email: contact.email || '',
          shortDescription: contact.shortDescription || '',
          sendWhatsAppDiary: contact.sendWhatsAppDiary || false,
          isMainContact: contact.isMainContact || false,
          photo: contact.photo || ''
        });
        
        if (contact.photo) {
          setPreviewPhoto(getPhotoUrl(contact.photo));
        }
      } else {
        setFormData({
          name: '',
          phone1: '',
          phone2: '',
          countryCode1: '55',
          countryCode2: '55',
          email: '',
          shortDescription: '',
          sendWhatsAppDiary: false,
          isMainContact: false,
          photo: ''
        });
        setPreviewPhoto('');
      }
      setErrors({});
    }
  }, [open, contact]);

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: event.target.type === 'checkbox' ? checked : value
    }));
    // Limpar erro quando o campo é alterado
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (value, data, field) => {
    const phoneNumber = value.replace(/\D/g, ''); // Remove todos os não-dígitos
    const countryCode = data.dialCode;
    const nationalNumber = phoneNumber.slice(data.dialCode.length);
    
    console.log(`Phone Change - Field: ${field}, Value: ${value}, Country Code: ${countryCode}, National Number: ${nationalNumber}`);
    
    setFormData(prev => ({
      ...prev,
      [field]: nationalNumber,
      [`countryCode${field === 'phone1' ? '1' : '2'}`]: countryCode
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamanho do arquivo (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          photo: 'A foto deve ter no máximo 5MB'
        }));
        e.target.value = '';
        return;
      }

      // Validar tipo do arquivo
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          photo: 'O arquivo deve ser uma imagem'
        }));
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        photo: file
      }));
      setPreviewPhoto(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, photo: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.phone1.trim()) {
      newErrors.phone1 = 'Telefone Principal é obrigatório';
    }

    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Descrição é obrigatória';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      
      // Concatenar código do país com os números de telefone
      const dataToSubmit = { ...formData };
      if (dataToSubmit.phone1) {
        dataToSubmit.phone1 = `+${dataToSubmit.countryCode1} ${dataToSubmit.phone1}`;
      }
      if (dataToSubmit.phone2) {
        dataToSubmit.phone2 = `+${dataToSubmit.countryCode2} ${dataToSubmit.phone2}`;
      }
      
      // Adicionar cada campo individualmente ao FormData
      Object.entries({
        name: dataToSubmit.name || '',
        phone1: dataToSubmit.phone1,
        phone2: dataToSubmit.phone2 || null,
        email: dataToSubmit.email || null,
        shortDescription: dataToSubmit.shortDescription || '',
        sendWhatsAppDiary: dataToSubmit.sendWhatsAppDiary ? 'true' : 'false',
        isMainContact: dataToSubmit.isMainContact ? 'true' : 'false'
      }).forEach(([key, value]) => {
        if (value !== null) {
          formDataToSend.append(key, value);
        }
      });

      // Adicionar a foto se for um arquivo
      if (formData.photo instanceof File) {
        formDataToSend.append('photo', formData.photo);
      }

      await onSubmit(formDataToSend);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar contato:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || 'Erro ao salvar contato'
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Novo Contato' : 'Editar Contato'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} display="flex" justifyContent="center">
            <Box position="relative">
              <Avatar
                src={previewPhoto}
                sx={{ width: 100, height: 100, mb: 2 }}
              >
                {!previewPhoto && formData.name.charAt(0).toUpperCase()}
              </Avatar>
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="label"
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: -16,
                }}
              >
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handlePhotoChange}
                />
                <PhotoCamera />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome do Contato"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição curta do contato"
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              error={!!errors.shortDescription}
              helperText={errors.shortDescription || 'Ex: filho, amigo, etc.'}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.phone1}>
              <PhoneInput
                country="br"
                value={formData.countryCode1 + formData.phone1}
                onChange={(value, data) => handlePhoneChange(value, data, 'phone1')}
                inputProps={{
                  required: true,
                  name: 'phone1'
                }}
                specialLabel="Telefone Principal"
                countryCodeEditable={true}
                enableSearch={true}
                searchPlaceholder="Buscar país..."
              />
              {errors.phone1 && <FormHelperText>{errors.phone1}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.phone2}>
              <PhoneInput
                country="br"
                value={formData.countryCode2 + (formData.phone2 || '')}
                onChange={(value, data) => handlePhoneChange(value, data, 'phone2')}
                inputProps={{
                  name: 'phone2'
                }}
                specialLabel="Telefone Secundário"
                countryCodeEditable={true}
                enableSearch={true}
                searchPlaceholder="Buscar país..."
              />
              {errors.phone2 && <FormHelperText>{errors.phone2}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="E-mail"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.sendWhatsAppDiary}
                  onChange={handleChange}
                  name="sendWhatsAppDiary"
                />
              }
              label="Enviar Diário via WhatsApp para o Telefone Principal?"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isMainContact}
                  onChange={handleChange}
                  name="isMainContact"
                />
              }
              label="É o contato principal?"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {mode === 'create' ? 'Criar' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactForm;
